import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { HistoryEvent, Memory, PersonSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { findFocus } from '@/lib/family-tree'
import { getHistory, getMemories, getPeople } from '@/lib/family'

import HeroSection from '@/components/landing/hero-section'
import FocusSection from '@/components/landing/focus-section'
import FamilySection from '@/components/landing/family-section'
import HistoryPreviewSection from '@/components/landing/history-preview-section'
import MemoriesSection from '@/components/landing/memories-section'
import ShareCtaSection from '@/components/landing/share-cta-section'

interface HomeProps {
    focus: PersonSummary | null
    relatives: Array<PersonSummary>
    peopleCount: number
    events: Array<HistoryEvent>
    memories: Array<Memory>
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async () => {
    try {
        const [people, events, memories] = await Promise.all([
            getPeople(),
            getHistory(3),
            getMemories({ limit: 3 })
        ])

        const focus = findFocus(people)

        return {
            props: {
                focus,
                relatives: people.filter(person => person.id !== focus?.id).slice(0, 6),
                peopleCount: people.length,
                events,
                memories
            }
        }
    } catch (error) {
        console.error('[home] could not load the family:', error instanceof Error ? error.message : 'unknown error')

        return { props: { focus: null, relatives: [], peopleCount: 0, events: [], memories: [] } }
    }
}

const Home: FC<HomeProps> = ({ focus, relatives, peopleCount, events, memories }): ReactNode => {
    return (
        <Layout>
            <Seo />

            <HeroSection />
            {focus && <FocusSection focus={focus} />}
            <FamilySection relatives={relatives} peopleCount={peopleCount} />
            <HistoryPreviewSection events={events} />
            {memories.length > 0 && <MemoriesSection memories={memories} />}
            <ShareCtaSection />
        </Layout>
    )
}

export default Home
